import { Button, Loader, Box } from '@coolblue-development/freddy';
import { datadogLogs } from '@datadog/browser-logs';
import { useState } from 'react';
import { Sort } from '@coolblue-development/icons';
import { EmailMessage } from '../../../../models/Email';
import { useEmailContext } from '../../../../contexts/EmailContext';
import { fetchEmailContent } from '../../../../services/fetchEmailContent';
import { useEnrichEmailWithContent } from '../../hooks/useEnrichEmailWithContent';
import { PREVIOUS_EMAILS_BATCH_SIZE } from '../../../../constants/email';
import EmailMessageItem from './Body/EmailMessageItem';

interface PreviousEmailsProps {
  messages: EmailMessage[];
  onAttachmentDownloadError?: () => void;
}

const PreviousEmails = ({ messages, onAttachmentDownloadError }: PreviousEmailsProps): JSX.Element | null => {
  const { selectedEmail, updateMessageContent } = useEmailContext();
  const enrichEmailWithContent = useEnrichEmailWithContent();
  const [isLoadingBatch, setIsLoadingBatch] = useState(false);
  // Previous emails start collapsed, so only the ones opened by hand or via "Expand all" are tracked.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  if (messages.length === 0) return null;

  const unloaded = [...messages].reverse().filter(m => !m.contentLoaded);
  const nextBatchSize = Math.min(unloaded.length, PREVIOUS_EMAILS_BATCH_SIZE);

  const loadedMessages = messages.filter(m => m.contentLoaded);
  const allExpanded = loadedMessages.length > 0 && loadedMessages.every(m => expandedIds.has(m.contactId));

  const toggleExpanded = (contactId: string): void =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (!next.delete(contactId)) next.add(contactId);
      return next;
    });

  const toggleAll = (): void => {
    // Lets us see in Datadog whether agents use the toggle instead of opening emails one by one.
    datadogLogs.logger.info('Expand/collapse all previous emails clicked', {
      previousEmailsToggle: true,
      action: allExpanded ? 'collapse' : 'expand',
      loadedCount: loadedMessages.length
    });
    setExpandedIds(allExpanded ? new Set() : new Set(loadedMessages.map(m => m.contactId)));
  };

  const loadNextBatch = async (): Promise<void> => {
    if (!selectedEmail || unloaded.length === 0 || isLoadingBatch) return;

    const batch = unloaded.slice(0, PREVIOUS_EMAILS_BATCH_SIZE);

    // `remaining` is what tells us whether PREVIOUS_EMAILS_BATCH_SIZE is too small: a non-zero
    // value means the agent has to click again to see the rest of the thread.
    datadogLogs.logger.info('Load previous emails clicked', {
      previousEmailsLoad: true,
      batchSize: batch.length,
      remaining: unloaded.length - batch.length
    });

    setIsLoadingBatch(true);
    try {
      const loaded = await Promise.all(
        batch.map(async message => {
          try {
            return { message, content: await fetchEmailContent(message.contactId) };
          } catch (error) {
            console.error('Failed to load previous email content:', error);
            return null;
          }
        })
      );

      loaded.forEach(result => {
        if (!result) return;
        updateMessageContent(
          selectedEmail,
          result.message.contactId,
          enrichEmailWithContent(result.message, result.content)
        );
      });
    } finally {
      setIsLoadingBatch(false);
    }
  };

  return (
    <Box sx={{ marginTop: '16px' }}>
      {isLoadingBatch && <Loader />}
      {!isLoadingBatch && (unloaded.length > 0 || loadedMessages.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: loadedMessages.length > 0 ? 'flex-start' : 'center',
            alignItems: 'center',
            margin: '0 16px 12px'
          }}
        >
          {unloaded.length > 0 && (
            <Button variant="tertiary" size="small" onClick={loadNextBatch}>
              Load {nextBatchSize} previous email{nextBatchSize > 1 ? 's' : ''}
            </Button>
          )}
          {loadedMessages.length > 0 && (
            <Button
              variant="tertiary"
              size="small"
              startIcon={<Sort />}
              onClick={toggleAll}
              sx={{ marginLeft: 'auto' }}
            >
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </Button>
          )}
        </Box>
      )}
      {messages
        .map((message, index) => ({ message, index }))
        .filter(({ message }) => message.contentLoaded)
        .map(({ message, index }) => (
          <EmailMessageItem
            key={message.contactId}
            message={message}
            index={index}
            isCollapsed={!expandedIds.has(message.contactId)}
            onToggleCollapsed={() => toggleExpanded(message.contactId)}
            onAttachmentDownloadError={onAttachmentDownloadError}
          />
        ))}
    </Box>
  );
};

export default PreviousEmails;
